import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useParams } from "react-router-dom";
import { getRateClassByResortId } from "../../../../../../../apis/property";
import {
  XIcon,
  CollectionIcon,
  TagIcon,
  DocumentTextIcon,
  FolderIcon,
  PlusCircleIcon,
} from "@heroicons/react/outline";

const CreateRateCategory = ({ isOpen, onClose, fetchResorts }) => {
  const [rateClass, setRateClass] = useState(null);
  const { id } = useParams();
  const baseUrl = process.env.REACT_APP_BASE_URL;
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    code: "",
    description: "",
    rate_class_id: "",
    resort_id: id,
  });

  const fetchRateClass = async () => {
    try {
      const response = await getRateClassByResortId(id);
      setRateClass(response.data.rate_classes);
    } catch (error) {
      console.log("Error fetching rate class");
    }
  };

  // The modal loads its option list once when it mounts.
  useEffect(() => {
    fetchRateClass();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const accessToken = localStorage.getItem("accessToken");

      const response = await fetch(`${baseUrl}/rate/category`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify(formData),
      });

      if (response.ok) {
        toast.success("Rate Category created successfully");
        setFormData({
          name: "",
          code: "",
          description: "",
          rate_class_id: "",
          resort_id: id,
        });
        onClose();
      } else {
        toast.error("Failed to create Rate Category");
        console.error("Failed to create Rate Category");
      }
    } catch (error) {
      toast.error("Error occurred while creating Rate Category");
      console.error("Error occurred while creating Rate Category:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <>
      <div className="fixed inset-0 z-50 overflow-y-auto">
        {/* Backdrop */}
        <div
          className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm transition-opacity"
          onClick={onClose}
        ></div>

        {/* Modal */}
        <div className="flex min-h-screen items-center justify-center p-4">
          <div className="relative w-full max-w-lg transform transition-all">
            <div className="modal-container">
              {/* Header */}
              <div className="modal-header">
                <div className="flex items-center space-x-3">
                  <div className="modal-icon">
                    <CollectionIcon className="h-6 w-6" />
                  </div>
                  <div>
                    <h3 className="modal-title">Add Rate Category</h3>
                    <p className="modal-subtitle">Create a new rate category</p>
                  </div>
                </div>
                <button onClick={onClose} className="modal-close-btn" type="button">
                  <XIcon className="h-5 w-5" />
                </button>
              </div>

              {/* Form */}
              <form onSubmit={handleSubmit}>
                <div className="modal-body">
                  <div className="form-grid">
                    {/* Category Name */}
                    <div className="form-group">
                      <label htmlFor="name" className="form-label">
                        <PlusCircleIcon className="form-label-icon" />
                        Category Name *
                      </label>
                      <input
                        type="text"
                        id="name"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        className="form-input"
                        placeholder="e.g., Weekend Category"
                        required
                      />
                    </div>

                    {/* Category Code */}
                    <div className="form-group">
                      <label htmlFor="code" className="form-label">
                        <TagIcon className="form-label-icon" />
                        Category Code *
                      </label>
                      <input
                        type="text"
                        id="code"
                        name="code"
                        value={formData.code}
                        onChange={handleChange}
                        className="form-input"
                        placeholder="e.g., WKND"
                        required
                      />
                    </div>

                    {/* Rate Class */}
                    <div className="form-group md:col-span-2">
                      <label htmlFor="rate_class_id" className="form-label">
                        <FolderIcon className="form-label-icon" />
                        Rate Class *
                      </label>
                      <select
                        id="rate_class_id"
                        name="rate_class_id"
                        value={formData.rate_class_id}
                        onChange={handleChange}
                        className="form-select"
                        required
                      >
                        <option value="">Select a rate class</option>
                        {rateClass?.map((item) => (
                          <option key={item.id} value={item.id}>
                            {item.name}
                          </option>
                        ))}
                      </select>
                    </div>

                    {/* Description */}
                    <div className="form-group md:col-span-2">
                      <label htmlFor="description" className="form-label">
                        <DocumentTextIcon className="form-label-icon" />
                        Description *
                      </label>
                      <textarea
                        id="description"
                        name="description"
                        value={formData.description}
                        onChange={handleChange}
                        className="form-textarea"
                        placeholder="Describe this rate category..."
                        rows="4"
                        required
                      />
                    </div>
                  </div>
                </div>

                {/* Footer */}
                <div className="modal-footer">
                  <button
                    type="button"
                    onClick={onClose}
                    className="btn-secondary"
                    disabled={isSubmitting}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="btn-primary"
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? (
                      <>
                        <svg className="animate-spin h-5 w-5 mr-2" viewBox="0 0 24 24">
                          <circle
                            className="opacity-25"
                            cx="12"
                            cy="12"
                            r="10"
                            stroke="currentColor"
                            strokeWidth="4"
                            fill="none"
                          />
                          <path
                            className="opacity-75"
                            fill="currentColor"
                            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                          />
                        </svg>
                        Creating...
                      </>
                    ) : (
                      "Add Rate Category"
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>

      {/* Styles */}
      <style jsx>{`
        .modal-container {
          background: white;
          border-radius: 1rem;
          box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
          overflow: hidden;
        }

        .modal-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 1.5rem;
          background: linear-gradient(135deg, #14b8a6, #0d9488);
          border-bottom: 1px solid rgba(255, 255, 255, 0.1);
        }

        .modal-icon {
          width: 3rem;
          height: 3rem;
          background: rgba(255, 255, 255, 0.2);
          backdrop-filter: blur(10px);
          border-radius: 0.75rem;
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
        }

        .modal-title {
          font-size: 1.25rem;
          font-weight: 700;
          color: white;
          margin: 0;
        }

        .modal-subtitle {
          font-size: 0.875rem;
          color: rgba(255, 255, 255, 0.9);
          margin: 0.25rem 0 0 0;
        }

        .modal-close-btn {
          padding: 0.5rem;
          background: rgba(255, 255, 255, 0.2);
          border: none;
          border-radius: 0.5rem;
          color: white;
          cursor: pointer;
          transition: all 0.2s ease;
        }

        .modal-close-btn:hover {
          background: rgba(255, 255, 255, 0.3);
          transform: scale(1.1);
        }

        .modal-body {
          padding: 2rem;
        }

        .form-grid {
          display: grid;
          grid-template-columns: repeat(2, 1fr);
          gap: 1.5rem;
        }

        .form-group {
          display: flex;
          flex-direction: column;
          gap: 0.5rem;
        }

        .form-label {
          display: flex;
          align-items: center;
          font-size: 0.875rem;
          font-weight: 600;
          color: #374151;
        }

        .form-label-icon {
          width: 1rem;
          height: 1rem;
          margin-right: 0.5rem;
          color: #14b8a6;
        }

        .form-input,
        .form-textarea,
        .form-select {
          width: 100%;
          padding: 0.75rem 1rem;
          border: 2px solid #e5e7eb;
          border-radius: 0.75rem;
          font-size: 0.875rem;
          transition: all 0.2s ease;
          background: #f9fafb;
        }

        .form-input:focus,
        .form-textarea:focus,
        .form-select:focus {
          outline: none;
          border-color: #14b8a6;
          background: white;
          box-shadow: 0 0 0 3px rgba(20, 184, 166, 0.1);
        }

        .form-textarea {
          resize: vertical;
          min-height: 100px;
        }

        .form-select {
          cursor: pointer;
        }

        .modal-footer {
          display: flex;
          justify-content: flex-end;
          gap: 0.75rem;
          padding: 1.5rem;
          background: #f9fafb;
          border-top: 1px solid #e5e7eb;
        }

        .btn-secondary {
          padding: 0.75rem 1.5rem;
          background: white;
          border: 2px solid #e5e7eb;
          border-radius: 0.75rem;
          font-weight: 600;
          color: #374151;
          cursor: pointer;
          transition: all 0.2s ease;
        }

        .btn-secondary:hover:not(:disabled) {
          background: #f3f4f6;
          border-color: #d1d5db;
        }

        .btn-secondary:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }

        .btn-primary {
          padding: 0.75rem 1.5rem;
          background: linear-gradient(135deg, #14b8a6, #0d9488);
          border: none;
          border-radius: 0.75rem;
          font-weight: 600;
          color: white;
          cursor: pointer;
          transition: all 0.2s ease;
          display: inline-flex;
          align-items: center;
          justify-content: center;
        }

        .btn-primary:hover:not(:disabled) {
          background: linear-gradient(135deg, #0d9488, #0f766e);
          transform: translateY(-2px);
          box-shadow: 0 10px 15px -3px rgba(20, 184, 166, 0.4);
        }

        .btn-primary:disabled {
          opacity: 0.7;
          cursor: not-allowed;
        }

        @media (max-width: 768px) {
          .form-grid {
            grid-template-columns: 1fr;
          }
        }
      `}</style>
    </>
  );
};

export default CreateRateCategory;
